// React and Router
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";

// Editor Related
import ReactQuill, { Quill } from "react-quill-new";
import { FONT_TOOLBAR_ENTRY } from "../../utils/quillFonts";
import ImageResize from "quill-image-resize-module-react";
import QuillTableBetter from "quill-table-better";

// Text Processing
import { diffWords } from "diff";
import Tesseract from "tesseract.js";
import * as pdfjsLib from "pdfjs-dist";

// Components
import Sidebar from "../shared/Sidebar.js";
import CommandCenterFilePickerModal from '../shared/CommandCenterFilePickerModal';
import ConflictResolver from "./ConflictResolver.js";
import DocumentHistoryModal from './DocumentHistoryModal.js';
import { useContextMenu } from "../shared/ContextMenu";
import Select from "react-select";
import { useQuickLinks } from "../../contexts/QuickLinksContext";
import { useAutoSnapshot } from "../../utils/snapshotUtils";
import useUnsavedChangesGuard from "../../utils/useUnsavedChangesGuard";
import useTemplateSnippets, { insertTemplateToolbarHandler, decorateInsertTemplateButton } from "../../utils/useTemplateSnippets";

// Firebase
import { auth } from "../../firebase.js";

// Task Reference
import "../shared/TaskReferenceBlot";
import TaskReferenceDropdown from "../shared/TaskReferenceDropdown";
import TaskDetailModal from "../shared/TaskDetailModal";
import { useTaskReference } from "../../utils/useTaskReference";
import useTaskDetailModal from "../../utils/useTaskDetailModal";
import "../shared/UserMentionBlot";
import UserMentionDropdown from "../shared/UserMentionDropdown";

// Styles
import "react-quill-new/dist/quill.snow.css";
import "react-quill/dist/quill.snow.css";
import "quill-better-table/dist/quill-better-table.css";
import "../../css/HomePage.css"
import "../../css/Create.css";
import "../../css/EditDocument.css";
import "../../css/DocumentationHomeComponent.css";
import "../../css/QuillOverrides.css";
import "../../css/Account.css";
import "../../css/TaskReference.css"
import "../../css/DocEditorMakeover.css";
import { notifyBlockedShareRecipients, readShareResponse } from "../../utils/sharePermissions";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

Quill.register({ "modules/table-better": QuillTableBetter }, true);
Quill.register("modules/imageResize", ImageResize);
const Inline = Quill.import("blots/inline");

class InlineCode extends Inline {
  static create() {
    let node = super.create();
    node.setAttribute("class", "inline-code");
    return node;
  }

  static formats(node) {
    return true;
  }
}

InlineCode.blotName = "inline-code";
InlineCode.tagName = "code";

Quill.register(InlineCode);

function EditDocument() {
  const { type, name, uid } = useParams();
  const [selectedType, setSelectedType] = useState("");
  const [newName, setNewName] = useState("");
  const [description, setDescription] = useState("");
  const [allDocumentation, setAllDocumentation] = useState([]);
  const [selectedParent, setSelectedParent] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [showTableOfContents, setShowTableOfContents] = useState(true); // BBD-58 — spaces only
  const [emails, setEmails] = useState([]);
  const [currentEmail, setCurrentEmail] = useState("");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Checking authentication...');
  const [hasPermission, setHasPermission] = useState(true);
  const [userUid, setUserUid] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [documentVersion, setDocumentVersion] = useState(null);
  const [showConflictResolver, setShowConflictResolver] = useState(false);
  const [latestContent, setLatestContent] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const quillRef = useRef(null);
  // "Insert template" snippet picker (BBD-108)
  const templateSnippets = useTemplateSnippets(quillRef);
  const { taskRefProps, userMentionProps } = useTaskReference(quillRef, { mode: "documentation", docType: type, docName: name });
  const { activeTask, open: openTaskModal, close: closeTaskModal } = useTaskDetailModal();
  const [customLink, setCustomLink] = useState("");
  const [customLinkTitle, setCustomLinkTitle] = useState("");
  const [discordLink, setDiscordLink] = useState("");
  const [linkedInLink, setLinkedInLink] = useState("");
  const [youtubeLink, setYoutubeLink] = useState("");
  const [githubLink, setGithubLink] = useState("");
  const [twitterLink, setTwitterLink] = useState("");
  const [blueskyLink, setBlueskyLink] = useState("");
  const { enhancedRightClickEnabled, snapshotFrequencyMinutes } = useQuickLinks();

  // Auto-snapshot functionality
  const documentData = {
    type: selectedType.toLowerCase(),
    name: newName,
    content: content,
    description: description,
    parentPage: selectedParent,
    isPublic: isPublic,
    completed: completed
  };
  const { startAutoSnapshot, stopAutoSnapshot, saveManualSnapshot } = useAutoSnapshot(
    documentData, 
    snapshotFrequencyMinutes, 
    !!userUid && !!newName && !!content && newName.trim() !== '' && content.trim() !== ''
  );

  const [showSplitView, setShowSplitView] = useState(window.innerWidth > 1200);
  const [selectedViewDoc, setSelectedViewDoc] = useState(null);
  const [selectedViewDocName, setSelectedViewDocName] = useState("");
  const [viewDocContent, setViewDocContent] = useState("");
  const [viewDocTitle, setViewDocTitle] = useState("");

  // Attachment states
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [ccPickerOpen, setCcPickerOpen] = useState(false);
  const [attachmentDescription, setAttachmentDescription] = useState("");
  const [currentAttachment, setCurrentAttachment] = useState(null);

  // Document history modal state
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [documentHistory, setDocumentHistory] = useState([]);
  const [snapshotSaveStatus, setSnapshotSaveStatus] = useState(null); // 'saving', 'success', 'error', null

  const maxLengths = {
    Space: 100,
    Page: 150,
  };

  const currentMaxLength = maxLengths[selectedType] || 100;

  useEffect(() => {
    const handleResize = () => {
      setShowSplitView(window.innerWidth > 1200);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  });

  // Theme cleanup on unmount



  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape" && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    document.addEventListener("keydown", handleEscKey);
    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isFullscreen]);

  // Warn before leaving with unsaved changes (BBD-56) — covers tab close /
  // refresh, in-app navigation (sidebar links, navigate() calls), and the
  // browser Back/Forward buttons.
  const { releaseGuard } = useUnsavedChangesGuard(hasUnsavedChanges);

  useEffect(() => {
    setLoadingMessage('Checking authentication...');
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUserUid(user.uid);
        setUserEmail(user.email);
      } else {
        setUserUid(null);
        setUserEmail(null);
        setHasPermission(false);
        setLoading(false);
      }
    });
  });

  useEffect(() => {
    if (uid && userUid && userEmail) {
      setLoadingMessage('Preparing document editor...');
      setSelectedType(
        String(type).charAt(0).toUpperCase() + String(type).slice(1)
      );
      setNewName(name);
      fetchDocumentContent();
      fetchPageDetailContent();
      fetchPermissionsForThisDocument();
    }
  }, [type, name, uid, userUid, userEmail]);

  // Add this after the ReactQuill component in the return statement
  useEffect(() => {
    if (quillRef.current) {
      // Get the toolbar element
      const toolbar = quillRef.current.getEditor().getModule('toolbar');
      
      // Find the undo and redo buttons and replace their content with Font Awesome icons
      const undoButton = toolbar.container.querySelector('.ql-undo');
      const redoButton = toolbar.container.querySelector('.ql-redo');
      
      if (undoButton) {
        undoButton.innerHTML = '<i class="fas fa-undo"></i>';
        undoButton.title = 'Undo';
      }
      
      if (redoButton) {
        redoButton.innerHTML = '<i class="fas fa-redo"></i>';
        redoButton.title = 'Redo';
      }
      decorateInsertTemplateButton(toolbar);
    }
  }, [quillRef.current]);

  useEffect(() => {
    if (type === "page" || type === "space") {
      setLoadingMessage('Loading documentation options...');
      populateDocumentation();
    }
  }, [selectedType]);

  // Auto-snapshot effect - start/stop based on document state
  useEffect(() => {
    if (userUid && newName && content && !loading) {
      startAutoSnapshot();
    } else {
      stopAutoSnapshot();
    }

    // Cleanup on unmount
    return () => {
      stopAutoSnapshot();
    };
  }, [userUid, newName, content, loading, snapshotFrequencyMinutes, startAutoSnapshot, stopAutoSnapshot]);

  const handleAddEmail = () => {
    if (currentEmail && !emails.some((item) => item.email === currentEmail)) {
      setEmails([...emails, { email: currentEmail, permission: "Can Edit" }]);
      setCurrentEmail("");
    } else {
      alert("This email has already been added.");
    }
  };

  const handleRemoveEmail = (index) => {
    const updatedEmails = [...emails];
    updatedEmails.splice(index, 1);
    setEmails(updatedEmails);
  };

  const handlePermissionChange = (index, newPermission) => {
    const updatedEmails = [...emails];
    updatedEmails[index].permission = newPermission;
    setEmails(updatedEmails);
  };

  const transformContent = (htmlContent) => {
    if (!htmlContent) return "";

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");

    // Demote headings two levels for display (h1→h3 … h4→h6) so all four
    // editor levels stay distinct while h1/h2 remain reserved for page
    // chrome. Deepest first so freshly created tags aren't re-demoted.
    [["h4", "h6"], ["h3", "h5"], ["h2", "h4"], ["h1", "h3"]].forEach(([from, to]) => {
      doc.querySelectorAll(from).forEach((heading) => {
        const demoted = document.createElement(to);
        demoted.innerHTML = heading.innerHTML;
        heading.replaceWith(demoted);
      });
    });

    doc.querySelectorAll("img").forEach((img) => {
      img.style.maxWidth = "100%";
      img.style.height = "auto";
      img.style.objectFit = "contain";
    });

    return doc.body.innerHTML;
  };

  const populateDocumentation = async () => {
    try {
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;
      const headers = token ? { 'Authorization': `${token}` } : {};
      const [spaceResponse, pageResponse] = await Promise.all([
        fetch(
          "https://bluebirddocumentationadmin.pythonanywhere.com/spaces/author/" +
            uid +
            "/",
          { headers }
        ),
        fetch(
          "https://bluebirddocumentationadmin.pythonanywhere.com/pages/author/" +
            uid +
            "/",
          { headers }
        ),
      ]);

      const spaceData =
        spaceResponse.status === 200
          ? await spaceResponse.json()
          : { spaces: [] };
      const pageData =
        pageResponse.status === 200 || pageResponse.status === 404
          ? await pageResponse.json()
          : [];
      const spaceOptions = Array.isArray(spaceData.spaces)
        ? spaceData.spaces.map((space) => ({
            label: space.name,
            value: space.id,
          }))
        : [];
      const pageOptions = Array.isArray(pageData.pages)
        ? pageData.pages.map((page) => ({
            label: page.page_name,
            value: page.id,
          }))
        : [];

      setAllDocumentation([...spaceOptions, ...pageOptions]);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleSelectedType = (e) => {
    setSelectedType(e.target.value);
  };
  const handleCompletedToggle = (e) => {
    setCompleted(e.target.checked);
  };
  const handleToggle = (e) => {
    setIsPublic(e.target.checked);
  };

  const fetchPermissionsForThisDocument = async () => {
    try {
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;
      const headers = token ? { 'Authorization': `${token}` } : {};
      const response = await fetch(
        `https://bluebirddocumentationadmin.pythonanywhere.com/permissions/${type}/${name}/`,
        { headers }
      );
      const data = await response.json();
      const existingPermissions = data.map((permission) => {
        let permissionLevel;
        if (permission.can_edit) {
          permissionLevel = "Can Edit";
        } else if (permission.can_view) {
          permissionLevel = "Can View";
        } else {
          permissionLevel = "";
        }

        return {
          email: permission.user_email,
          permission: permissionLevel,
        };
      });

      setEmails(existingPermissions);
    } catch (error) {
      console.error("Error fetching permission data: ", error);
    }
  };

  useEffect(() => {
    if (!selectedViewDoc) return;
    setLoadingMessage('Fetching side view document...');
    const fetchViewDocument = async () => {
      try {
        const user = auth.currentUser;
        const token = user ? await user.getIdToken() : null;
        if (!token) {
          console.error("User is not authenticated.");
          return;
        }
        const response = await fetch(
          `https://bluebirddocumentationadmin.pythonanywhere.com/documentation/page/${selectedViewDocName}/${uid}/`,
          {
            method: "GET",
            headers: {
              "requesting-user-uid": userUid,
              "requesting-user-email": userEmail,
              "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          setViewDocContent(transformContent(data.content));
          setViewDocTitle(data.page_name);
        } else {
          const response2 = await fetch(
            `https://bluebirddocumentationadmin.pythonanywhere.com/documentation/space/${selectedViewDocName}/${uid}/`,
            {
              method: "GET",
              headers: {
                "requesting-user-uid": userUid,
                "requesting-user-email": userEmail,
                "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,
              },
            }
          );
          if (response2.ok) {
            const data2 = await response2.json();
            setViewDocContent(transformContent(data2.content));
            setViewDocTitle(data2.name);
          } else {
            console.error("Failed to fetch document content.");
          }
        }
      } catch (error) {
        console.error("Error fetching document: ", error);
      }
    };
    fetchViewDocument();
  }, [selectedViewDoc]);

  const fetchDocumentContent = async () => {
    if (!userUid || !userEmail) {
      setLoading(false);
      return;
    }

    setLoadingMessage('Fetching document content...');

    try {
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;
      if (!token) {
        console.error("User is not authenticated.");
        return;
      }
      setLoadingMessage('Loading document for editing...');
      const mode = "edit";
      const response = await fetch(
        `https://bluebirddocumentationadmin.pythonanywhere.com/documentation/${type}/${name}/${uid}/`,
        {
          method: "GET",
          headers: {
            "requesting-user-uid": userUid,
            "requesting-user-email": userEmail,
            "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,
            "mode": mode,
          },
        }
      );

      setLoading(false);

      if (response.status === 403) {
        setHasPermission(false);
        return;
      }

      if (response.status === 404) {
        setHasPermission(false);
        setLoading(false);
        return;
      }

      const data = await response.json();
      setLoadingMessage('Processing document data...');
      setDescription(data.description);
      setCompleted(data.completed);
      setShowTableOfContents(data.show_table_of_contents !== false);
      //  setContent(data.content);
      setLoadingMessage('Initializing editor...');
      setTimeout(() => {
        if (quillRef.current) {
          const editor = quillRef.current.getEditor();
      
          // Clean up the HTML first (remove <temporary> tags)
          let cleanHtml = data.content;
          cleanHtml = cleanHtml.replace(/<temporary[^>]*>.*?<\/temporary>/g, "");
      
          // Convert cleaned HTML to Delta
          const delta = editor.clipboard.convert({ html: cleanHtml });
      
          // Get current cursor position (optional but nice for UX)
          const range = editor.getSelection();
      
          // Update editor content using Delta
          editor.updateContents(delta, "user");
      
          // Set cursor position properly (otherwise cursor will jump randomly)
          if (range) {
            editor.setSelection(
              delta.length() - (range.length || 0),
              0,
              "silent"
            );
          }
      
          editor.scrollSelectionIntoView();
      
          // Save clean HTML if needed (optional)
          setContent(editor.root.innerHTML);
        }
      }, 1000);
      
      setDocumentVersion(data.version);
      setYoutubeLink(data.youtube_channel);
      setCustomLink(data.custom_domain);
      setCustomLinkTitle(data.custom_domain_title);
      setDiscordLink(data.discord_server);
      setLinkedInLink(data.linkedin_link);
      setGithubLink(data.github_link);
      setTwitterLink(data.twitter_link);
      setBlueskyLink(data.bluesky_link);
      
      // Set attachment data if it exists
      if (data.attachments && data.attachments.length > 0) {
        setCurrentAttachment(data.attachments[0]); // Get the first attachment
        setAttachmentDescription(data.attachments[0].description || "");
      }
      
      // Set history data if it exists
      if (data.history && Array.isArray(data.history)) {
        setDocumentHistory(data.history);
      }
    } catch (error) {
      setLoading(false);
      console.error("Error fetching document data: ", error);
    }
  };

  const fetchPageDetailContent = async () => {
    if (!uid) return;
    const token = await auth.currentUser.getIdToken();
    try {
      const response = await fetch(
        `https://bluebirddocumentationadmin.pythonanywhere.com/page-details/${type}/${name}/${uid}/`,
        {
          headers: {
            'Authorization': `${token}`
          }
        }
      );
      const data = await response.json();
      setSelectedParent(data.parent_page);
      setIsPublic(data.is_public);
    } catch (error) {
      console.error("Error fetching page detail content: ", error);
    }
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [FONT_TOOLBAR_ENTRY],
          [{ header: 1 }, { header: 2 }, { header: 3 }, { header: 4 }],
          [{ size: ["small", false, "large"] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          [{ script: "sub" }, { script: "super" }],
          [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
          [{ indent: "-1" }, { indent: "+1" }],
          [{ align: [] }],
          ["blockquote", "code-block"],
          ["link", "image", "video"],
          ["inline-code"],
          ["formula"],
          ["table-better"], // table button
          ["clean"],
          ["image"],
          ["insert-template"], // Insert a snippet template (BBD-108)
          ["undo", "redo"], // Add undo and redo buttons
        ],
        handlers: {
          undo: function() {
            this.quill.history.undo();
          },
          redo: function() {
            this.quill.history.redo();
          },
          "insert-template": insertTemplateToolbarHandler
        }
      },
      history: {
        delay: 1000,
        maxStack: 100,
        userOnly: true
      },
      imageResize: {
        parchment: Quill.import("parchment"),
        modules: ["Resize", "DisplaySize"],
      },
      table: false,
      "table-better": {
        language: "en_US",
        menus: [
          "column",
          "row",
          "merge",
          "table",
          "cell",
          "wrap",
          "copy",
          "delete",
        ],
        toolbarTable: true,
        // Add these tableCreationAttributes for responsive tables
        tableCreationAttributes: {
          className: 'responsive-table',
          style: 'width: 100%; max-width: 100%;'
        }
      },
      keyboard: {
        bindings: QuillTableBetter.keyboardBindings,
      },
    }),
    []
  );
  const handleSubmit = async () => {
      // Validation: Prevent duplicate document names (excluding current document)
      const nameExists = allDocumentation.some(doc => (doc.label || "").toLowerCase() === newName.trim().toLowerCase() && doc.label !== name);
      if (nameExists) {
        alert("A document with this name already exists. Please choose a different name.");
        return;
      }
    // Validation checks
    if (!newName || newName.trim() === "") {
      alert("Please enter a name for your document.");
      return;
    }

    // Check that name only contains letters and numbers
    if (!/^[a-zA-Z0-9\s\.\-\_]+$/.test(newName)) {
      alert(
        "Document name must contain only letters, numbers, spaces, periods, dashes, and underscores."
      );
      return;
    }

    // Check for parent document if type is Page
    if (selectedType === "Page" && !selectedParent) {
      alert("Please select a parent for the page.");
      return;
    }

    // Set loading to true immediately after validation passes
    setIsSubmitting(true);
    setLoadingMessage('Preparing document update...');

    try {
      // Get Firebase token
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;
      if (!token) {
        console.error("User is not authenticated.");
        setIsSubmitting(false);
        alert("Authentication error. Please sign in again.");
        return;
      }

      setLoadingMessage('Updating permissions...');
      await updatePermissionTableEntries();

      setLoadingMessage('Saving document changes...');

      // Extract mentioned user UIDs from Quill HTML content
      const mentionedUids = (() => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(content, 'text/html');
        const spans = doc.querySelectorAll('[data-user-uid]');
        const uids = [...new Set([...spans].map(s => s.getAttribute('data-user-uid')).filter(Boolean))];
        return uids.filter(u => u !== userUid);
      })();

      // Check if we need to use FormData for file upload
      if (attachmentFile) {
        const formData = new FormData();
        formData.append('type', selectedType);
        formData.append('isPublic', isPublic);
        formData.append('emails', JSON.stringify(emails));
        formData.append('content', content);
        formData.append('newName', newName);
        formData.append('author', uid);
        formData.append('completed', completed);
        formData.append('showTableOfContents', showTableOfContents ? 'true' : 'false');
        formData.append('description', description);
        formData.append('parentPage', selectedParent);
        formData.append('version', documentVersion);
        formData.append('customLink', customLink);
        formData.append('customLinkTitle', customLinkTitle);
        formData.append('twitterLink', twitterLink);
        formData.append('blueskyLink', blueskyLink);
        formData.append('discordLink', discordLink);
        formData.append('linkedInLink', linkedInLink);
        formData.append('githubLink', githubLink);
        formData.append('youtubeLink', youtubeLink);
        formData.append('attachment', attachmentFile);
        formData.append('attachmentDescription', attachmentDescription);
        formData.append('mentionedUids', JSON.stringify(mentionedUids));

        const response = await fetch(
          `https://bluebirddocumentationadmin.pythonanywhere.com/documentation/edit/${type}/${name}/`,
          {
            method: "PATCH",
            headers: {
              "requesting-user-uid": userUid,
              "requesting-user-email": userEmail,
              "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,
            },
            body: formData,
          }
        );

        if (response.ok) {
          setLoadingMessage('Document saved successfully! Redirecting...');
          setHasUnsavedChanges(false);
          releaseGuard();
          navigate(
            `/documentation/${selectedType.toLowerCase()}/${newName}/${uid}/`
          );
        } else if (response.status === 409) {
          // Handle conflict resolution
          try {
            const latestResponse = await fetch(
              `https://bluebirddocumentationadmin.pythonanywhere.com/documentation/${type}/${name}/${uid}/`,
              {
                method: "GET",
                headers: {
                  "requesting-user-uid": userUid,
                  "requesting-user-email": userEmail,
                  "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,
                  "mode": "edit",
                },
              }
            );

            if (latestResponse.ok) {
              setIsSubmitting(false);
              alert(
                "There appears to be a conflict between your current changes, and changes made by another user while editing this document. Please resolve them and submit again."
              );
              const latestData = await latestResponse.json();
              setLatestContent(latestData.content);
              setDocumentVersion(latestData.version);
              setShowConflictResolver(true);
            } else {
              console.error("Failed to fetch latest version, status:", latestResponse.status);
              throw new Error("Failed to fetch the latest version of the document.");
            }
          } catch (fetchError) {
            console.error("Error fetching latest document version:", fetchError);
            throw new Error("Failed to fetch the latest version of the document.");
          }
        } else {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.message || `Failed to submit edits. Status: ${response.status}`;
          console.error("Edit submission failed (FormData):", {
            status: response.status,
            statusText: response.statusText,
            errorData
          });
          throw new Error(errorMessage);
        }
      } else {
        let hasPreviousAttachment = false;
        if (currentAttachment) {
          hasPreviousAttachment = true;
        }
        const data = {
          type: selectedType,
          isPublic: isPublic,
          emails: emails,
          content: content,
          newName: newName,
          author: uid,
          completed: completed,
          showTableOfContents: showTableOfContents,
          description: description,
          parentPage: selectedParent,
          version: documentVersion,
          customLink: customLink,
          customLinkTitle: customLinkTitle,
          twitterLink: twitterLink,
          blueskyLink: blueskyLink,
          discordLink: discordLink,
          linkedInLink: linkedInLink,
          githubLink: githubLink,
          youtubeLink: youtubeLink,
          hasPreviousAttachment: hasPreviousAttachment,
          mentionedUids: mentionedUids
        };

        const response = await fetch(
          `https://bluebirddocumentationadmin.pythonanywhere.com/documentation/edit/${type}/${name}/`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              "requesting-user-uid": userUid,
              "requesting-user-email": userEmail,
              "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,
            },
            body: JSON.stringify(data),
          }
        );

        if (response.ok) {
          setLoadingMessage('Document saved successfully! Redirecting...');
          setHasUnsavedChanges(false);
          releaseGuard();
          navigate(
            `/documentation/${selectedType.toLowerCase()}/${newName}/${uid}/`
          );
        } else if (response.status === 409) {
          // Fetch the latest document content
          try {
            const latestResponse = await fetch(
              `https://bluebirddocumentationadmin.pythonanywhere.com/documentation/${type}/${name}/${uid}/`,
              {
                method: "GET",
                headers: {
                  "requesting-user-uid": userUid,
                  "requesting-user-email": userEmail,
                  "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,
                  "mode": "edit",
                },
              }
            );

            if (latestResponse.ok) {
              setIsSubmitting(false); // Stop loading before showing conflict resolver
              alert(
                "There appears to be a conflict between your current changes, and changes made by another user while editing this document. Please resolve them and submit again."
              );
              const latestData = await latestResponse.json();
              setLatestContent(latestData.content);
              setDocumentVersion(latestData.version);
              setShowConflictResolver(true);
            } else {
              console.error("Failed to fetch latest version, status:", latestResponse.status);
              throw new Error("Failed to fetch the latest version of the document.");
            }
          } catch (fetchError) {
            console.error("Error fetching latest document version:", fetchError);
            throw new Error("Failed to fetch the latest version of the document.");
          }
        } else {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.message || `Failed to submit edits. Status: ${response.status}`;
          console.error("Edit submission failed (JSON):", {
            status: response.status,
            statusText: response.statusText,
            errorData
          });
          throw new Error(errorMessage);
        }
      }
    } catch (error) {
      console.error("Error submitting edits: ", error);
      alert(error.message || "An error occurred while updating the document.");
      setIsSubmitting(false); // Stop loading on error
    }
  };

  const loggedOutOptions = [
    {
      label: "Login",
      icon: <i className="fas fa-sign-in-alt"></i>,
      onClick: () => navigate("/login"),
    },
    {
      label: "Register",
      icon: <i className="fas fa-user-plus"></i>,
      onClick: () => navigate("/register"),
    },
    {
      label: "Try My Notebook",
      icon: <i className="fas fa-book"></i>,
      onClick: () => navigate("/my-notebook"),
    },
    {
      label: "Visit YouTube Channel",
      icon: <i className="fab fa-youtube"></i>,
      onClick: () => window.open("https://www.youtube.com/@schoolofintology4062", "_blank", "noopener,noreferrer"),
    },
    {
      label: "Join Discord",
      icon: <i className="fab fa-discord"></i>,
      onClick: () => window.open("https://discord.gg/DYkESHwgVA", "_blank", "noopener,noreferrer"),
    },
  ];

const loggedInOptions = [
  {
    label: "Submit Edits",
    icon: <i className="fas fa-paper-plane"></i>, // Optional: add an icon
    onClick: handleSubmit, // This will trigger your submit logic
  },
      {
        label: "Home",
        icon: <i className="fas fa-home"></i>,
        onClick: () => window.open("/", "_blank", "noopener,noreferrer"),
      },
  {
    label: "Your Documents",
    onClick: () => window.open("/documentation", "_blank", "noopener,noreferrer"),
  },
  {
    label: "Your Walkthroughs",
    onClick: () => window.open("/walkthroughs", "_blank", "noopener,noreferrer"),
  },
  {
    label: "Your Notebook",
    onClick: () => window.open("/my-notebook", "_blank", "noopener,noreferrer"),
  },
  {
    label: "Your Workspace",
    onClick: () => window.open("/workspaces", "_blank", "noopener,noreferrer"),
  },
  {
    label: "Account Settings",
    onClick: () => window.open(`/account/${uid}`, "_blank", "noopener,noreferrer"),
  },
];

  const menuOptions = uid ? loggedInOptions : loggedOutOptions;
  const { showContextMenu, ContextMenuComponent, handleContextMenu } = useContextMenu(menuOptions);

  const handleContentChange = (value) => {
    setContent(value);
    setHasUnsavedChanges(true);
  };

  const updatePermissionTableEntries = async () => {
    const data = {
      name: newName,
      documentationType: selectedType,
      userEmail: emails,
      isPublic: isPublic,
      documentUid: uid,
      documentUrl: `documentation/${selectedType.toLowerCase()}/${newName}/${uid}/`,
      description: description,
    };

    try {
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;
      const response = await fetch(
        "https://bluebirddocumentationadmin.pythonanywhere.com/permissions/edit/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token && { 'Authorization': `${token}` })
          },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || "Failed to update permissions.");
      }

      // BBD-73: a 201 can still be partial — addresses on either side of a
      // block are skipped and reported back in blocked_emails.
      notifyBlockedShareRecipients(await readShareResponse(response));
      
    } catch (error) {
      console.error("Error updating permissions: ", error);
      throw error; // Propagate the error to be caught by the parent function
    }
  };

  const formatExtractedText = (text) => {
    // Detect paragraphs by newlines or specific patterns
    const paragraphs = text.split(/\n(?=[A-Z])/).map((paragraph) => {
      // Replace single newlines with <br /> for line breaks inside paragraphs
      return `<p>${paragraph.trim().replace(/\n/g, "<br />")}</p>`;
    });

    return paragraphs.join("");
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];

    if (!file) return;

    try {
      let rawText = "";
      if (file.type === "application/pdf") {
        rawText = await extractTextFromPDF(file);
      } else if (file.type.startsWith("image/")) {
        rawText = await extractTextFromImage(file);
      } else {
        alert("Unsupported file type. Please upload an image or PDF.");
        return;
      }

      // Format the extracted text
      const formattedText = formatExtractedText(rawText);

      // Set the formatted content into the editor
      setContent((prevContent) => `${prevContent}${formattedText}`);
    } catch (error) {
      console.error("Error processing file:", error);
    }
  };

  const extractTextFromImage = async (file) => {
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
      reader.onload = async () => {
        try {
          const {
            data: { text },
          } = await Tesseract.recognize(reader.result, "eng", {
            logger: (info) => console.log(info), // Monitor progress
          });
          resolve(text);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const extractTextFromPDF = async (file) => {
    const pdf = await pdfjsLib.getDocument(URL.createObjectURL(file)).promise;
    let fullText = "";
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map((item) => item.str).join(" ");
      fullText += `\nPage ${i}:\n${pageText}`;
    }
    return fullText;
  };

    const handleAttachmentUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // List of allowed extensions
        const allowedExtensions = ['.zip', '.pdf', '.jpeg', '.png', '.mp3', '.mp4'];

        // Get the file extension including the dot
        const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;

        // Check if the file extension is in the allowed list
        if (!allowedExtensions.includes(fileExtension)) {
            alert('Only ZIP, PDF, JPEG, PNG, MP3, and MP4 files are allowed as attachments');
            event.target.value = ''; // Clear the input
            return;
        }

        // Validate file size (8MB limit)
        const maxSize = 8 * 1024 * 1024; // 8MB in bytes
        if (file.size > maxSize) {
            alert('File size exceeds the maximum limit of 8MB');
            event.target.value = ''; // Clear the input
            return;
        }

        setAttachmentFile(file);
        setHasUnsavedChanges(true);
    };

  const removeAttachment = () => {
    setAttachmentFile(null);
    setAttachmentDescription("");
    // Clear the file input
    const fileInput = document.getElementById('attachment-upload');
    if (fileInput) {
      fileInput.value = '';
    }
    setHasUnsavedChanges(true);
  };

  const removeCurrentAttachment = () => {
    setCurrentAttachment(null);
    setAttachmentDescription("");
    setHasUnsavedChanges(true);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const truncateFileName = (fileName, maxLength = 40) => {
    if (fileName.length <= maxLength) return fileName;
    
    const extension = fileName.substring(fileName.lastIndexOf('.'));
    const nameWithoutExtension = fileName.substring(0, fileName.lastIndexOf('.'));
    const truncatedName = nameWithoutExtension.substring(0, maxLength - extension.length - 3) + '...';
    
    return truncatedName + extension;
  };

  const handleHomeButtonClick = () => {
    navigate("/");
  };
  const handleLoginButtonClick = () => {
    navigate("/login");
  };
  const handleRegisterButtonClick = () => {
    navigate("/register");
  };
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };
  
  const handleManualSnapshot = async () => {
    setSnapshotSaveStatus('saving');
    try {
      const success = await saveManualSnapshot();
      setSnapshotSaveStatus(success ? 'success' : 'error');
      setTimeout(() => setSnapshotSaveStatus(null), 3000); // Clear status after 3 seconds
    } catch (error) {
      console.error('Error saving manual snapshot:', error);
      setSnapshotSaveStatus('error');
      setTimeout(() => setSnapshotSaveStatus(null), 3000);
    }
  };
  
  const handleHistoryButtonClick = () => {
    setShowHistoryModal(true);
  };

  if (loading) {
    return (
        <>
            <div className="loading-animation-div" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }}>
                <div className="coffee-cup-loader">
                    <div className="cup">
                        <div className="cup-body"></div>
                        <div className="cup-handle"></div>
                        <div className="cup-base"></div>
                        <div className="cup-shadow"></div>
                        <div className="steam steam1"></div>
                        <div className="steam steam2"></div>
                        <div className="steam steam3"></div>
                    </div>
                </div>
                <p style={{ color: '#5B92E5', fontSize: '1.1rem', marginTop: 16 }}>{loadingMessage}</p>
            </div>
        </>
    );
  }

  // Add this right after the opening return statement
  if (isSubmitting) {
    return (
        <>
            <div className="loading-animation-div" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }}>
                <div className="coffee-cup-loader">
                    <div className="cup">
                        <div className="cup-body"></div>
                        <div className="cup-handle"></div>
                        <div className="cup-base"></div>
                        <div className="cup-shadow"></div>
                        <div className="steam steam1"></div>
                        <div className="steam steam2"></div>
                        <div className="steam steam3"></div>
                    </div>
                </div>
                <p style={{ color: '#5B92E5', fontSize: '1.1rem', marginTop: 16 }}>{loadingMessage || 'Submitting...'}</p>
            </div>
        </>
    );
  }

  if (loading === false && hasPermission === false) {
    return (
      <div className="DocumentationMainPage__div">
        {/* <Sidebar /> */}
        <div className="DocumentationMainPage__main-content">
          <p className="large">
            It looks like you don't have permission to edit this document.
          </p>
          <p className="medium">
            <strong>You might just need to sign in</strong>. If this is your
            document, make sure you're signed in. If this issue persists, this
            could be a bug on our end. We are very sorry for the inconvenience;
            please let us know this has happened.
          </p>
          <p className="medium">
            <strong>If this is not your document</strong>, check with the
            original author of the document to see if you have correct
            permissions.
          </p>
          <button
            onClick={handleHomeButtonClick}
            style={{ marginRight: "10px" }}
            className="ConflictResolver__button"
          >
            Home
          </button>
          <button
            onClick={handleLoginButtonClick}
            style={{ marginRight: "10px" }}
            className="ConflictResolver__button"
          >
            Login
          </button>
          <button
            onClick={handleRegisterButtonClick}
            className="ConflictResolver__button"
          >
            Register
          </button>
        </div>
      </div>
    );
  }

  const handleContentClick = (e) => {
    const chip = e.target.closest('.task-reference');
    if (!chip) return;
    openTaskModal(
      chip.getAttribute('data-task-id'),
      chip.getAttribute('data-board-id'),
      chip.getAttribute('data-task-db-id')
    );
  };

  return (
    <div
        className="Create__div DocEditor"
      onContextMenu={
        enhancedRightClickEnabled ? handleContextMenu : undefined
      }
    >
      <Sidebar />

      <div className="Create__main-content">
        <div className="EditDocument__header-section">
          <h2>Edit Space/Page</h2>
          <button 
            style={{ marginTop: "2px" }} 
            className="Account__navigation-buttons"
            onClick={handleHistoryButtonClick}
          >
            <i className="fa-solid fa-clock-rotate-left"></i> History
          </button>
        </div>

        <div className="Create__required-fields-div">

        <div className="Create__form-group Create__required-field-margin-top">
          {type === "page" && (
            <>
              <label className="Create__required-field">Edit Document Type: </label>
              <select
                className="form-select Create__input Create__required-field-select"
                aria-label="Default select example"
                name="selected_type"
                value={selectedType}
                onChange={handleSelectedType}
              >
                <option value="Page">Page</option>
                <option value="Space">Space</option>
              </select>
            </>
          )}
          {type === "space" && (
            <>
              <label className="Create__required-field">Edit Document Type: </label>
              <select
                className="form-select Create__input Create__required-field-select"
                aria-label="Default select example"
                name="selected_type"
                value={selectedType}
                onChange={handleSelectedType}
              >
                <option value="Space">Space</option>
                <option value="Page">Page</option>
              </select>
            </>
          )}
        </div>

        {selectedType === "Page" && (
          <div className="Create__form-group">
            <label style={{ paddingTop: "3px" }} className="Create__required-field">
              Select a parent space or page:{" "}
            </label>
            <Select
              options={allDocumentation.filter(
                (option) => option.label !== newName && option.label !== name
              )}
              value={allDocumentation.find(
                (option) => option.value === selectedParent
              )}
              onChange={(option) =>
                setSelectedParent(option ? option.label : "")
              }
              placeholder={selectedParent}
              isSearchable={true}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                container: (provided) => ({
                  ...provided,
                  minWidth: '150px'
                }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 })
              }}
            />
          </div>
        )}

        <div className="Create__form-group">
          <label className="Create__required-field">Edit Name: </label>
          <input
            type="text"
            id="name"
            className="Create__input-short-field Create__required-field-select"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            maxLength={currentMaxLength}
            placeholder={`Max ${currentMaxLength} characters`}
          />
        </div>

        </div>

        <div className="Create__form-group">
          <label>Edit Description: </label>
          <input
            type="text"
            id="description"
            className="Create__input-full-field"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="Create__form-group-helper-text">
          <i
            className="fas fa-info-circle Create__info-icon"
            title="Information"
          ></i>
          <p className="Create__helper-text">
            This description is what invited users will see about this
            space/page.
          </p>
        </div>

        <div className="Create__form-group">
          <label>Edit Public Status: </label>
          <label className="Create__switch">
            <input type="checkbox" checked={isPublic} onChange={handleToggle} />
            <span className="Create__slider round"></span>
          </label>
        </div>
        <div className="Create__form-group-helper-text">
          <i
            className="fas fa-info-circle Create__info-icon"
            title="Information"
          ></i>
          <p className="Create__helper-text">
            Public content is viewable by anyone that has this document's URL.
          </p>
        </div>

        <div className="Create__form-group">
          <label>Publish this page upon finishing changes?</label>
          <label className="Create__switch">
            <input
              type="checkbox"
              checked={completed}
              onChange={handleCompletedToggle}
            />
            <span className="Create__slider round"></span>
          </label>
        </div>
        <div className="Create__form-group-helper-text">
          <i
            className="fas fa-info-circle Create__info-icon"
            title="Information"
          ></i>
          <p className="Create__helper-text">
            Whether the document is public or not, this document will not be{" "}
            <strong>viewable</strong> by anyone; including added collaborators
            that have "Can View" permission. Collaborators that have "Can Edit"
            permission can edit this document.
          </p>
        </div>

        {selectedType === "Space" && (
          <>
            <div className="Create__form-group">
              <label>Show a table of contents on this space?</label>
              <label className="Create__switch">
                <input
                  type="checkbox"
                  checked={showTableOfContents}
                  onChange={(e) => {
                    setShowTableOfContents(e.target.checked);
                    setHasUnsavedChanges(true);
                  }}
                />
                <span className="Create__slider round"></span>
              </label>
            </div>
            <div className="Create__form-group-helper-text">
              <i
                className="fas fa-info-circle Create__info-icon"
                title="Information"
              ></i>
              <p className="Create__helper-text">
                When enabled, viewers see an "In this space" section near the
                top of the space listing the pages inside it that they have
                access to.
              </p>
            </div>
          </>
        )}

        <div
          className="Create__form-group"
          style={{ flexDirection: "column", marginBottom: "60px" }}
        >
          <label>Modify/Add Collaborators</label>
          {emails.map((item, index) => (
            <div
              key={index}
              className="Create__email-item"
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "10px",
              }}
            >
              <span>{item.email}</span>
              <select
                value={item.permission}
                onChange={(e) => handlePermissionChange(index, e.target.value)}
                style={{ marginLeft: "10px", padding: "5px" }}
              >
                <option value="Can Edit" disabled={uid != userUid}>
                  Can Edit
                </option>
                <option value="Can View" disabled={uid != userUid}>
                  Can View
                </option>
              </select>
              {uid === userUid ? (
                <button
                  onClick={() => handleRemoveEmail(index)}
                  style={{
                    marginLeft: "10px",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <i
                    className="fas fa-trash-alt"
                    title="Remove Email"
                    style={{ color: "white" }}
                  ></i>
                </button>
              ) : (
                <></>
              )}
            </div>
          ))}
          {uid === userUid ? (
            <>
              <div className="Create__email-input-container">
                <input
                  type="email"
                  className="Create__email-input"
                  placeholder="Enter E-mail Address"
                  value={currentEmail}
                  onChange={(e) => setCurrentEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddEmail()}
                />
                <button
                  onClick={handleAddEmail}
                  className="Create__add-email-btn"
                >
                  <i className="fas fa-plus"></i>
                </button>
              </div>
              <div className="Create__form-group-helper-text">
                <i
                  className="fas fa-info-circle Create__info-icon"
                  title="Information"
                ></i>
                <p className="Create__helper-text">
                  As of this current version, any added permissions will{" "}
                  <strong>not</strong> automatically be added to any future
                  child documents that are created.
                </p>
              </div>
            </>
          ) : (
            <div className="Create__form-group-helper-text">
              <i
                className="fas fa-info-circle Create__info-icon"
                title="Information"
              ></i>
              <p className="Create__helper-text">
                As of this current release, only original authors can edit
                permissions.
              </p>
            </div>
          )}
        </div>

        {/* <div className="Create__form-group">
          <label>Upload Notes: </label>
          <input
            type="file"
            className="Create__file-upload"
            accept=".jpeg,.jpg,.png,.pdf"
            onChange={handleFileUpload}
            multiple={false}
          />
        </div>
        <div
          className="Create__form-group-helper-text"
          style={{ marginBottom: "60px" }}
        >
          <i
            className="fas fa-info-circle Create__info-icon"
            title="Information"
          ></i>
          <p className="Create__helper-text">
            You can upload any typed notes or handwritten notes, and we'll do
            our best to try to extract text from it. Anything that is extracted
            will be placed in the text editor below. This feature is cetainly
            very early in development; we're working to improve this feature
            throughout the releases.
          </p>
        </div> */}

        {/* Attachment Section */}
        <div className="Create__form-group" style={{ marginTop: "20px" }}>
          <label>
            File Attachment (Optional)
          </label>

          {/* File input for new attachment */}
          <input
            type="file"
            id="attachment-upload"
            accept=".zip,.pdf,.jpeg,.png,.mp3,.mp4"
            onChange={handleAttachmentUpload}
            className="Create__file-input"
          />
          <button
            type="button"
            onClick={() => setCcPickerOpen(true)}
            style={{ display: 'block', marginBottom: '10px', marginRight: '12px', background: 'none', border: '1px solid #0d6efd', color: '#0d6efd', borderRadius: '5px', padding: '6px 12px', cursor: 'pointer', whiteSpace: 'nowrap', alignSelf: 'flex-start' }}
          >
            <i className="fas fa-box-open"></i> Choose from Command Center
          </button>

          {/* Show selected new attachment */}
          {attachmentFile && (
            <div className="Create__selected-file">
              <button
                type="button"
                onClick={removeAttachment}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#dc3545",
                    fontSize: "16px"
                  }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          )}
        </div>
          {/* Show current attachment if exists */}
          {currentAttachment && !attachmentFile && (
            <div className="Create__current-attachment">
                <div className="Create__attachment-details">
                  <span className="Create__attachment-name" title={currentAttachment.file_name}>
                    <i className="fas fa-file-archive" style={{ marginRight: '8px' }}></i>
                    {truncateFileName(currentAttachment.file_name)}
                  </span>
                  <span className="Create__attachment-size" style={{ marginLeft: '10px', color: '#666', fontSize: '0.9em' }}>
                    ({formatFileSize(currentAttachment.file_size)})
                  </span>
                </div>
                <button
                  type="button"
                  onClick={removeCurrentAttachment}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#dc3545",
                    fontSize: "16px"
                  }}
                  title="Remove current attachment"
                >
                  <i className="fas fa-times"></i>
                </button>
            </div>
          )}

          <div className="Create__form-group-helper-text" style={{ marginBottom: "60px" }}>
            <i className="fas fa-info-circle Create__info-icon" title="Information"></i>
            <p className="Create__helper-text">
              You can attach one ZIP, PDF, JPEG, PNG, MP3, or MP4 file to your document. The file size limit is 8MB.
              Users will be able to download this attachment when viewing the document.
            </p>
          </div>

        <input
          type="text"
          placeholder="Custom Link Title"
          value={customLinkTitle}
          onChange={(e) => setCustomLinkTitle(e.target.value)}
          className="Account__input-custom-title"
        />
        <input
          id="customLinkInput"
          type="text"
          placeholder="Custom Link Domain"
          value={customLink}
          onChange={(e) => setCustomLink(e.target.value)}
          className="Account__edit-input"
        />

        <div className="Account__link-divs">
          <i class="fa-brands fa-discord Account__icons"></i>
          <input
            type="text"
            value={discordLink}
            placeholder="Discord Server Link"
            onChange={(e) => setDiscordLink(e.target.value)}
            className="Account__edit-input"
          />
        </div>

        <div className="Account__link-divs">
          <i class="fa-brands fa-square-x-twitter Account__icons"></i>
          <input
            type="text"
            value={twitterLink}
            placeholder="X Profile Link"
            onChange={(e) => setTwitterLink(e.target.value)}
            className="Account__edit-input"
          />
        </div>

        <div className="Account__link-divs">
          <i class="fa-brands fa-bluesky Account__icons"></i>
          <input
            type="text"
            value={blueskyLink}
            placeholder="Bluesky Profile Link"
            onChange={(e) => setBlueskyLink(e.target.value)}
            className="Account__edit-input"
          />
        </div>

        <div className="Account__link-divs">
          <i class="fa-brands fa-linkedin Account__icons"></i>
          <input
            type="text"
            value={linkedInLink}
            placeholder="LinkedIn Profile Link"
            onChange={(e) => setLinkedInLink(e.target.value)}
            className="Account__edit-input"
          />
        </div>

        <div className="Account__link-divs">
          <i class="fa-brands fa-youtube Account__icons"></i>
          <input
            type="text"
            value={youtubeLink}
            placeholder="YouTube Channel Link"
            onChange={(e) => setYoutubeLink(e.target.value)}
            className="Account__edit-input"
          />
        </div>

        <div className="Account__link-divs">
          <i class="fa-brands fa-square-github Account__icons"></i>
          <input
            type="text"
            value={githubLink}
            placeholder="GitHub Profile Link"
            onChange={(e) => setGithubLink(e.target.value)}
            className="Account__edit-input"
          />
        </div>

        <div
          className="Create__form-group-helper-text"
          style={{ marginBottom: "60px" }}
        >
          <i
            className="fas fa-info-circle Create__info-icon"
            title="Information"
          ></i>
          <p className="Create__helper-text">
            These are links that users will see when visiting this document. The
            links are automatically pulled from what's saved in your account
            settings. Feel free to edit any of the links if you don't want to
            use what's pulled from your account settings.{" "}
            <strong>
              If you don't want your saved link(s) to show for this particular
              document,{" "}
            </strong>
            just remove the contents of that input; until you see the
            placeholder text again.
          </p>
        </div>

        {showSplitView && (
          <div className="EditDocument__split-view-selector">
            <label>Select Side View Document:</label>
            <Select
              options={allDocumentation}
              value={allDocumentation.find(
                (doc) => doc.label === selectedViewDocName
              )} // Match by document name
              onChange={(option) => {
                setSelectedViewDoc(option ? option.value : null); // Store ID
                setSelectedViewDocName(option ? option.label : ""); // Store document name
              }}
              placeholder="Choose a document..."
              isSearchable={true}
            />
          </div>
        )}

        <div className="EditDocument__edit-document-container">
          {/* Main Editor Section */}
          <div
            className={`EditDocument__editor-section ${
              selectedViewDoc ? "split" : ""
            }`}
          >
            <div
              className={`EditDocument__quill-container ${
                isFullscreen ? "fullscreen-mode" : ""
              }`}
            >
              {showConflictResolver ? (
                <ConflictResolver
                  localContent={content}
                  latestContent={latestContent}
                  onResolve={(resolvedContent) => {
                    setContent(resolvedContent);
                    setShowConflictResolver(false);
                  }}
                />
              ) : (
                <>
                <div className="QuillOverrides__content-formatting" onClick={handleContentClick}>
                  <ReactQuill
                    value={content}
                    ref={quillRef}
                    onChange={handleContentChange}
                    modules={modules}
                    style={{
                      height: isFullscreen ? "calc(100vh - 50px)" : "70vh",
                    }}
                  />
                  <TaskReferenceDropdown {...taskRefProps} />
                  <UserMentionDropdown {...userMentionProps} />
                  </div>
                  <button
                    onClick={toggleFullscreen}
                    className="EditDocument__fullscreen-btn EditDocument__fullscreen-btn-corner"
                    title={
                      isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"
                    }
                    style={{ bottom: isFullscreen ? "20px" : "-18px" }}
                  >
                    <i
                      className={`fas ${
                        isFullscreen ? "fa-compress-alt" : "fa-expand-alt"
                      }`}
                    ></i>
                  </button>
                </>
              )}
            </div>
          </div>

          {/* View Document Section (Only Shown If A Document is Selected) */}
          {selectedViewDoc && showSplitView && (
            <div className="EditDocument__view-document-section">
              <div className="EditDocument__view-document-header">
                <p className="medium">
                  <strong>Viewing:</strong> <i>{viewDocTitle}</i>
                </p>
                <button
                  onClick={() => setSelectedViewDoc(null)}
                  className="EditDocument__close-btn"
                >
                  ✖
                </button>
              </div>
              <div className="EditDocument__view-document-content">
                <div dangerouslySetInnerHTML={{ __html: viewDocContent }} />
              </div>
            </div>
          )}
        </div>

        <div className="EditDocument__action-buttons">
          <button onClick={handleSubmit} className="submit-button">
            Submit
          </button>
        </div>
      </div>
      {enhancedRightClickEnabled && ContextMenuComponent}
      
      <DocumentHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        documentUid={uid}
        documentTitle={newName || name}
        historyData={documentHistory}
      />

      {activeTask && (
        <TaskDetailModal
          boardTaskId={activeTask.boardTaskId}
          boardId={activeTask.boardId}
          taskDbId={activeTask.taskDbId}
          onClose={closeTaskModal}
        />
      )}

      {templateSnippets.element}
      <CommandCenterFilePickerModal
        isOpen={ccPickerOpen}
        onClose={() => setCcPickerOpen(false)}
        onSelect={(file) => setAttachmentFile(file)}
        allowedExtensions={['.zip', '.pdf', '.jpeg', '.png', '.mp3', '.mp4']}
        maxSizeBytes={8 * 1024 * 1024}
      />
    </div>
  );
}

export default EditDocument;
